import { Dayjs } from 'dayjs';

import { MapUtils } from '../../../utility/map-utils.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { TimeZone } from '../../../time/timezone.js';

import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { cloneData, cloneList, isSameData, isSameList } from '../../dto/data-model-utils.js';

import { BusinessModuleRef } from '../business-module-ref.js';
import { JSONUtils } from '../../../utility/json-utils.js';
import { VersionRange } from '../../core/version-range.js';

export enum AdSetStatus { Active = 'Active', Inactive = 'Inactive' }

export class AdSet extends DataModel {
  static readonly TYPE_ID: string = '3ddc04ab49c947a6bbeb5fd49888f1c9';
  static readonly SCHEMA_VERSION: number = 3;

  private constructor(
    public readonly adSetId: string,
    public readonly adSetName: string,
    public readonly campaignId: string,
    public readonly campaignName: string,
    public readonly adAccountId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly eventId: string,
    public readonly eventName: string,
    public readonly startDate: Dayjs,
    public readonly endDate: Dayjs,
    public readonly status: AdSetStatus,
    public readonly budget: number,
    public readonly includedCustomAudienceId: string | null,
    public readonly excludedCustomAudienceId: string | null,
    public readonly alreadyPurchasedTicket: Array<string>,
  ) {
    super();
  }

  get $typeId(): string {
    return AdSet.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(AdSet.SCHEMA_VERSION);
  }

  static create(args: {
    adSetId: string,
    adSetName: string,
    campaignId: string,
    campaignName: string,
    adAccountId: string,
    businessRef: BusinessModuleRef,
    eventId: string,
    eventName: string,
    startDate: Dayjs,
    endDate: Dayjs,
    status: AdSetStatus,
    budget: number,
    includedCustomAudienceId?: string | null,
    excludedCustomAudienceId?: string | null,
    alreadyPurchasedTicket?: Array<string>
  }): AdSet {
    return new AdSet(
        args.adSetId,
        args.adSetName,
        args.campaignId,
        args.campaignName,
        args.adAccountId,
        args.businessRef,
        args.eventId,
        args.eventName,
        args.startDate,
        args.endDate,
        args.status,
        args.budget,
        args.includedCustomAudienceId ?? null,
        args.excludedCustomAudienceId ?? null,
        args.alreadyPurchasedTicket ?? []
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): AdSet {
    return AdSet.create({
      adSetId: MapUtils.getString(map, 'adSetId'),
      adSetName: MapUtils.getString(map, 'adSetName'),
      campaignId: MapUtils.getString(map, 'campaignId'),
      campaignName: MapUtils.getString(map, 'campaignName'),
      adAccountId: MapUtils.getString(map, 'adAccountId'),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      eventId: MapUtils.getString(map, 'eventId'),
      eventName: MapUtils.getString(map, 'eventName'),
      startDate: context.decodeDate(MapUtils.getInteger(map, 'startDate')),
      endDate: context.decodeDate(MapUtils.getInteger(map, 'endDate')),
      status: toEnumFromString(MapUtils.getString(map, 'status'), AdSetStatus),
      budget: MapUtils.getDouble(map, 'budget'),
      includedCustomAudienceId: MapUtils.getStringOrNull(map, 'includedCustomAudienceId'),
      excludedCustomAudienceId: MapUtils.getStringOrNull(map, 'excludedCustomAudienceId'),
      alreadyPurchasedTicket: MapUtils.getList<string>(map, 'alreadyPurchasedTicket', (value) => JSONUtils.decodeString(value)),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'adSetId': this.adSetId,
      'adSetName': this.adSetName,
      'campaignId': this.campaignId,
      'campaignName': this.campaignName,
      'adAccountId': this.adAccountId,
      'businessRef': this.businessRef.encode(),
      'eventId': this.eventId,
      'eventName': this.eventName,
      'startDate': context.encodeDate(this.startDate),
      'endDate': context.encodeDate(this.endDate),
      'status': fromEnumAsString(this.status),
      'budget': this.budget,
      'includedCustomAudienceId': this.includedCustomAudienceId,
      'excludedCustomAudienceId': this.excludedCustomAudienceId,
      'alreadyPurchasedTicket': this.alreadyPurchasedTicket,
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    adSetId = this.adSetId,
    adSetName = this.adSetName,
    campaignId = this.campaignId,
    campaignName = this.campaignName,
    adAccountId = this.adAccountId,
    businessRef = cloneData(this.businessRef),
    eventId = this.eventId,
    eventName = this.eventName,
    startDate = TimeZone.cloneDate(this.startDate),
    endDate = TimeZone.cloneDate(this.endDate),
    status = this.status,
    budget = this.budget,
    includedCustomAudienceId = this.includedCustomAudienceId,
    excludedCustomAudienceId = this.excludedCustomAudienceId,
    alreadyPurchasedTicket = cloneList(this.alreadyPurchasedTicket),
  }: {
    adSetId?: string,
    adSetName?: string,
    campaignId?: string,
    campaignName?: string,
    adAccountId?: string,
    businessRef?: BusinessModuleRef,
    eventId?: string,
    eventName?: string,
    startDate?: Dayjs,
    endDate?: Dayjs,
    status?: AdSetStatus,
    budget?: number,
    includedCustomAudienceId?: string | null,
    excludedCustomAudienceId?: string | null,
    alreadyPurchasedTicket?: Array<string>
  } = {}): this {
    return AdSet.create({
      adSetId,
      adSetName,
      campaignId,
      campaignName,
      adAccountId,
      businessRef,
      eventId,
      eventName,
      startDate,
      endDate,
      status,
      budget,
      includedCustomAudienceId,
      excludedCustomAudienceId,
      alreadyPurchasedTicket,
    }) as this;
  }

  isSame(other: AdSet): boolean {
    if(other === this) {
      return true;
    }

    return other.adSetId === this.adSetId &&
      other.adSetName === this.adSetName &&
      other.campaignId === this.campaignId &&
      other.campaignName === this.campaignName &&
      other.adAccountId === this.adAccountId &&
      isSameData(other.businessRef, this.businessRef) &&
      other.eventId === this.eventId &&
      other.eventName === this.eventName &&
      TimeZone.isSameDay(other.startDate, this.startDate) &&
      TimeZone.isSameDay(other.endDate, this.endDate) &&
      other.status === this.status &&
      other.budget === this.budget &&
      other.includedCustomAudienceId === this.includedCustomAudienceId &&
      other.excludedCustomAudienceId === this.excludedCustomAudienceId &&
      isSameList<string>(other.alreadyPurchasedTicket, this.alreadyPurchasedTicket);
  }
}

export class AdSetBuilder extends DataModelBuilder<AdSet> {
  build(context: SerializationContext, map: SerializedData): AdSet {
    return AdSet.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(AdSet.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(AdSet.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version: number = versionRange.to;

    // IncludedCustomAudienceId became nullable
    if(version < 2) {
      version = 2;
    }

    // Added excludedCustomAudienceId and adAccountId
    if(version < 3) {
      MapUtils.set(map, 'excludedCustomAudienceId', null);
      MapUtils.set(map, 'adAccountId', 'ZzyEI2MyQqtu2lBdKPBp');

      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
